import React, { useContext, useMemo, useState, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { makeStyles } from 'tss-react/mui';
import {
  TEDLRFakturaAttestAttestTypEnum,
  TEDLRFakturaAttestSTATUSEnum,
} from '../../../../../../../globalQueryTypes';
import { useIsSmallScreen } from '../../../../../../../Hooks/useFullscreenView';
import { mergeBusinessDataFormValues } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import { getFormUpdateValue } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormValueUtils';
import { BusinessDataFormContext } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import FakturaDelContext from '../FakturaDelContextManager/FakturaDelContext';
import { EDAutoCompleteEdit } from '../../../../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import EDChip from '../../../../../../../BaseComponents/Chip/EDChip';
import Dialog from '../../../../../../../BaseComponents/Dialog/EDDialog';
import EDOverlay from '../../../../../../../BaseComponents/Overlay/EDOverlay';
import {
  FakturaDelQuery_node_TEDLRFakturaDel__Associations_Fakturaattest_All,
  FakturaDelQuery_node_TEDLRFakturaDel__Associations_Fakturaattest_All__Associations_Attestratt,
} from '../../Queries/_QueryTypes/FakturaDelQuery';
import {
  AllCertifiers,
  AllCertifiersVariables,
  AllCertifiers_db_LRAttestratt_All,
} from './Queries/_QueryTypes/AllCertifiers';
import AllCertifiersQuery from './Queries/AllCertifiersQuery.gql';
import EDTooltip from '../../../../../../../BaseComponents/Tooltip/EDTooltip';
import EDAvenyProgress from '../../../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';
// eslint-disable-next-line aveny/mui-import-rule
import { AutocompleteRenderGetTagProps } from '@mui/material/Autocomplete';
import {
  getCurrentCertifiers,
  haveUnCertifiedCertifiers,
  isAttestanterUnique,
} from './Utils/FakturaAttestHandleCertifiersModalUtils';
import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';
import { isEmpty } from 'ramda';
import EDBox from '../../../../../../../BaseComponents/EDBox/EDBox';
import EDDivider from '../../../../../../../BaseComponents/Divider/EDDivider';
import { useDeepCompareEffect } from 'use-deep-compare';
import { OnChange } from '../../../../../../../Components/Attributes/EditableAttribute/EditComponentTypes';

interface IProps {
  dbName: string;
  onClose: () => void;
  open: boolean;
}

const useStyles = makeStyles()(theme => ({
  paper: { width: '50%' },
  savingOverlay: {
    right: 'calc(50% - 20px)',
    bottom: 'calc(50% - 20px)',
  },
  tag: {
    backgroundColor: theme.palette.grey[300],
    height: 'auto',
    margin: `${theme.spacing(0.5)} ${theme.spacing(0.25)}`,
    minHeight: theme.spacing(3),
  },
  inputRoot: {
    '&[class*="MuiOutlinedInput-root"]': {
      paddingTop: theme.spacing(0.5),
    },
  },
}));

const FakturaAttestHandleCertifiersModal: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const { onClose } = props;
  const dataformContext = useContext(BusinessDataFormContext);
  const fakturaDelContext = useContext(FakturaDelContext);
  const { addFakturaattest, deleteFakturaattest, getAttestanter } =
    fakturaDelContext;
  const certifiersQuery = useQuery<AllCertifiers, AllCertifiersVariables>(
    AllCertifiersQuery,
    {
      variables: {
        dbName: props.dbName ?? '',
        certifyGroup: dataformContext?.data?.ATTEST_GRUPP_ID ?? '',
      },
    }
  );
  const isSmallScreen = useIsSmallScreen();
  const { classes } = useStyles();

  const [decisionValue, setDecisionValue] = useState<
    AllCertifiers_db_LRAttestratt_All[]
  >([]);
  const [controlValue, setControlValue] = useState<
    AllCertifiers_db_LRAttestratt_All[]
  >([]);

  const [receiverValue, setReceiverValue] = useState<
    AllCertifiers_db_LRAttestratt_All[]
  >([]);

  const [loading, setLoading] = useState(true);

  const allCertifierData: AllCertifiers_db_LRAttestratt_All[] = useMemo(
    () =>
      certifiersQuery?.data?.db?.LRAttestratt_All?.filter(
        (attestratt: AllCertifiers_db_LRAttestratt_All) =>
          !attestratt._Associations?.Attestomrade?.BORTTAGEN &&
          attestratt.Attestgruppid === dataformContext?.data?.ATTEST_GRUPP_ID
      ) ?? [],
    [
      certifiersQuery?.data?.db?.LRAttestratt_All,
      dataformContext?.data?.ATTEST_GRUPP_ID,
    ]
  );

  const receiptCertificates = useMemo(
    () => getAttestanter(TEDLRFakturaAttestAttestTypEnum.MOTTAGNINGSATTEST),
    [getAttestanter]
  );

  const decisionCertificates = useMemo(
    () => getAttestanter(TEDLRFakturaAttestAttestTypEnum.BESLUTSATTEST),
    [getAttestanter]
  );

  const controlCertificates = useMemo(
    () => getAttestanter(TEDLRFakturaAttestAttestTypEnum.KONTROLLATTEST),
    [getAttestanter]
  );

  const receiptCertifiers = useMemo(
    () =>
      receiptCertificates.map(certifier => {
        return certifier._Associations
          ?.Attestratt as FakturaDelQuery_node_TEDLRFakturaDel__Associations_Fakturaattest_All__Associations_Attestratt;
      }),
    [receiptCertificates]
  );

  const decisionCertifiers = useMemo(
    () =>
      decisionCertificates.map(certifier => {
        return certifier._Associations
          ?.Attestratt as FakturaDelQuery_node_TEDLRFakturaDel__Associations_Fakturaattest_All__Associations_Attestratt;
      }),
    [decisionCertificates]
  );

  const controlCertifiers = useMemo(
    () =>
      controlCertificates.map(certifier => {
        return certifier._Associations
          ?.Attestratt as FakturaDelQuery_node_TEDLRFakturaDel__Associations_Fakturaattest_All__Associations_Attestratt;
      }),
    [controlCertificates]
  );

  const currentReceiptCertifiers = useMemo(
    () =>
      getCurrentCertifiers(
        certifiersQuery.loading,
        allCertifierData,
        receiptCertifiers
      ),
    [certifiersQuery.loading, allCertifierData, receiptCertifiers]
  );

  const currentDecisionCertifiers = useMemo(
    () =>
      getCurrentCertifiers(
        certifiersQuery.loading,
        allCertifierData,
        decisionCertifiers
      ),
    [certifiersQuery.loading, allCertifierData, decisionCertifiers]
  );

  const currentControlCertifiers = useMemo(
    () =>
      getCurrentCertifiers(
        certifiersQuery.loading,
        allCertifierData,
        controlCertifiers
      ),
    [certifiersQuery.loading, allCertifierData, controlCertifiers]
  );

  const isUpdateButtonDisabled = useCallback((): React.ReactNode => {
    let disabledReason = [];
    if (certifiersQuery.loading || loading) {
      disabledReason.push(<EDBox>Laddar...</EDBox>);
      return disabledReason;
    }

    const morphedReceiverValue = receiverValue.map(value => value.id);
    const morphedDecisionValue = decisionValue.map(value => value.id);
    const morphedControlValue = controlValue.map(value => value.id);

    const morphedReceiptCertifiers = receiptCertifiers.map(value => value.id);
    const morphedDecisionCertifiers = decisionCertifiers.map(value => value.id);
    const morphedControlCertifiers = controlCertifiers.map(value => value.id);

    const receiptDifference = morphedReceiptCertifiers
      .filter(x => !morphedReceiverValue.includes(x))
      .concat(
        morphedReceiverValue.filter(x => !morphedReceiptCertifiers.includes(x))
      );

    const decisionDifference = morphedDecisionCertifiers
      .filter(x => !morphedDecisionValue.includes(x))
      .concat(
        morphedDecisionValue.filter(x => !morphedDecisionCertifiers.includes(x))
      );
    const controlDifference = morphedControlCertifiers
      .filter(x => !morphedControlValue.includes(x))
      .concat(
        morphedControlValue.filter(x => !morphedControlCertifiers.includes(x))
      );

    if (
      receiptDifference.length === 0 &&
      decisionDifference.length === 0 &&
      controlDifference.length === 0
    ) {
      disabledReason.push(<EDBox>Inga ändringar är gjorda</EDBox>);
      return disabledReason;
    }
    if (
      !(
        haveUnCertifiedCertifiers(decisionValue, decisionCertificates) ||
        haveUnCertifiedCertifiers(receiverValue, receiptCertificates)
      )
    ) {
      disabledReason.push(
        <EDBox>Det finns inga oattesterade attestanter kvar</EDBox>
      );
    }
    if (!isAttestanterUnique(decisionValue, receiverValue)) {
      disabledReason.push(
        <EDBox>
          En attestant kan inte både vara mottagningsattestant och
          beslutattestant
        </EDBox>
      );
    }
    if (!isAttestanterUnique(receiverValue, controlValue)) {
      disabledReason.push(
        <EDBox>
          En attestant kan inte både vara mottagningsattestant och
          kontrollattestant
        </EDBox>
      );
    }
    if (!isAttestanterUnique(decisionValue, controlValue)) {
      disabledReason.push(
        <EDBox>
          En attestant kan inte både vara beslutattestant och kontrollattestant
        </EDBox>
      );
    }

    return disabledReason;
  }, [
    certifiersQuery.loading,
    loading,
    receiverValue,
    decisionValue,
    controlValue,
    receiptCertifiers,
    decisionCertifiers,
    controlCertifiers,
    decisionCertificates,
    receiptCertificates,
  ]);

  const getOptionDisabled = useCallback(
    (
        certificates: FakturaDelQuery_node_TEDLRFakturaDel__Associations_Fakturaattest_All[]
      ) =>
      (option: AllCertifiers_db_LRAttestratt_All) => {
        const optionHasCertified = certificates.some(
          value =>
            value?.STATUS === TEDLRFakturaAttestSTATUSEnum.ATTESTERAD &&
            value?.AttestantSign ===
              option?._Associations?.Attestant?.SIGNATUR &&
            value?._Associations?.Attestratt?._Associations?.Attestomrade
              ?.KOD === option?._Associations.Attestomrade?.KOD
        );

        return optionHasCertified;
      },
    []
  );

  const getOptionLabel = useCallback(
    (option: AllCertifiers_db_LRAttestratt_All): string => {
      let label = option?._Associations?.Attestant?.BENAMNING;

      if (option?._Associations?.Attestomrade?.KOD != null) {
        label += ` - ${option._Associations?.Attestomrade?.KOD}`;
      }
      return label ?? '';
    },
    []
  );

  const getOptionSelected = useCallback(
    (
      option: AllCertifiers_db_LRAttestratt_All,
      value: AllCertifiers_db_LRAttestratt_All
    ) =>
      option?._Associations?.Attestant?.SIGNATUR ===
        value?._Associations?.Attestant?.SIGNATUR &&
      option?._Associations?.Attestomrade?.KOD ===
        value?._Associations?.Attestomrade?.KOD,
    []
  );

  const getCertifiedOption = useCallback(
    (
        option: AllCertifiers_db_LRAttestratt_All,
        certificateType: TEDLRFakturaAttestAttestTypEnum
      ) =>
      (
        certificate: FakturaDelQuery_node_TEDLRFakturaDel__Associations_Fakturaattest_All
      ) =>
        certificate.STATUS === TEDLRFakturaAttestSTATUSEnum.ATTESTERAD &&
        certificate.AttestantSign ===
          option._Associations?.Attestant?.SIGNATUR &&
        certificate._Associations?.Attestratt?._Associations?.Attestomrade
          ?.KOD === option._Associations.Attestomrade?.KOD &&
        certificate.AttestTyp === certificateType,
    []
  );

  const getDisabledReason = useCallback(
    (
      option: AllCertifiers_db_LRAttestratt_All,
      certificates: FakturaDelQuery_node_TEDLRFakturaDel__Associations_Fakturaattest_All[],
      tagValue: AllCertifiers_db_LRAttestratt_All[],
      certifyType: TEDLRFakturaAttestAttestTypEnum
    ) => {
      let reasons: string[] = [];

      const alreadyCertifiedTooltip = 'Denna attestant har redan attesterat';

      if (loading) {
        reasons.push('Loading');
      }

      if (certificates.some(getCertifiedOption(option, certifyType))) {
        reasons.push(alreadyCertifiedTooltip);
      }

      if (
        certifyType === TEDLRFakturaAttestAttestTypEnum.KONTROLLATTEST &&
        currentControlCertifiers.some(currentControlCertifier => {
          return currentControlCertifier.id === option.id;
        })
      ) {
        reasons.push('Redan upplagda kontrollattestanter går inte att ta bort');
      }

      return reasons;
    },
    [currentControlCertifiers, getCertifiedOption, loading]
  );

  const getReceiverValue: OnChange<AllCertifiers_db_LRAttestratt_All, true> =
    useCallback((newValue, event) => {
      if (newValue != null) {
        if (Array.isArray(newValue)) {
          setReceiverValue([...newValue]);
        }
      }
    }, []);

  const getDecisionValue: OnChange<AllCertifiers_db_LRAttestratt_All, true> =
    useCallback((newValue, event) => {
      if (newValue != null) {
        if (Array.isArray(newValue)) {
          setDecisionValue([...newValue]);
        }
      }
    }, []);

  const getControlValue: OnChange<AllCertifiers_db_LRAttestratt_All, true> =
    useCallback((newValue, event) => {
      if (newValue != null) {
        if (Array.isArray(newValue)) {
          setControlValue([...newValue]);
        }
      }
    }, []);

  const getChip =
    (
      getTagProps: AutocompleteRenderGetTagProps,
      tagValue: AllCertifiers_db_LRAttestratt_All[],
      certificates: FakturaDelQuery_node_TEDLRFakturaDel__Associations_Fakturaattest_All[],
      certifyType: TEDLRFakturaAttestAttestTypEnum
    ) =>
    (option: AllCertifiers_db_LRAttestratt_All, index: number) => {
      let label = getOptionLabel(option);

      const disabledReasons = getDisabledReason(
        option,
        certificates,
        tagValue,
        certifyType
      );

      const chipDisabled = disabledReasons.length !== 0;

      return (
        <>
          {chipDisabled ? (
            <EDTooltip
              key={index}
              title={disabledReasons.map(reason => (
                <>
                  {reason}
                  <br />
                </>
              ))}
              enableClickOnTouch={chipDisabled}
              enterDelay={500}
              enterTouchDelay={500}
            >
              <span>
                <EDChip
                  label={label}
                  {...getTagProps({ index })}
                  deleteIcon={chipDisabled ? <> </> : undefined}
                  disabled={chipDisabled}
                  variant="outlined"
                />
              </span>
            </EDTooltip>
          ) : (
            <EDChip
              label={label}
              {...getTagProps({ index })}
              variant="outlined"
            />
          )}
        </>
      );
    };

  const onUpdateClick = useCallback(() => {
    if (dataformContext == null) {
      return;
    }

    setLoading(true);
    let newValues = [];

    const deletedMottagningsAttestanter = currentReceiptCertifiers.filter(
      x => !receiverValue.includes(x)
    );

    const addedMottagningsAttestanter = receiverValue.filter(certifier => {
      return !currentReceiptCertifiers.some(
        exsistingCertifier => exsistingCertifier.id === certifier.id
      );
    });

    if (addedMottagningsAttestanter.length > 0) {
      newValues.push(
        addFakturaattest(
          addedMottagningsAttestanter,
          TEDLRFakturaAttestAttestTypEnum.MOTTAGNINGSATTEST
        )
      );
    }

    if (deletedMottagningsAttestanter.length > 0) {
      newValues.push(
        deleteFakturaattest(
          deletedMottagningsAttestanter,
          TEDLRFakturaAttestAttestTypEnum.MOTTAGNINGSATTEST
        )
      );
    }

    const deletedBeslutAttestanter = currentDecisionCertifiers.filter(
      x => !decisionValue.includes(x)
    );

    const addedBeslutAttestanter = decisionValue.filter(certifier => {
      return !currentDecisionCertifiers.some(
        exsistingCertifier => exsistingCertifier.id === certifier.id
      );
    });

    if (addedBeslutAttestanter.length > 0) {
      newValues.push(
        addFakturaattest(
          addedBeslutAttestanter,
          TEDLRFakturaAttestAttestTypEnum.BESLUTSATTEST
        )
      );
    }

    if (deletedBeslutAttestanter.length > 0) {
      newValues.push(
        deleteFakturaattest(
          deletedBeslutAttestanter,
          TEDLRFakturaAttestAttestTypEnum.BESLUTSATTEST
        )
      );
    }

    const deletedKontrollAttestanter = currentControlCertifiers.filter(
      x => !controlValue.includes(x)
    );

    const addedKontrollAttestanter = controlValue.filter(certifier => {
      return !currentControlCertifiers.some(
        exsistingCertifier => exsistingCertifier.id === certifier.id
      );
    });

    if (addedKontrollAttestanter.length > 0) {
      newValues.push(
        addFakturaattest(
          addedKontrollAttestanter,
          TEDLRFakturaAttestAttestTypEnum.KONTROLLATTEST
        )
      );
    }

    if (deletedKontrollAttestanter.length > 0) {
      newValues.push(
        deleteFakturaattest(
          deletedKontrollAttestanter,
          TEDLRFakturaAttestAttestTypEnum.KONTROLLATTEST
        )
      );
    }

    dataformContext?.saveForm(
      mergeBusinessDataFormValues(
        getFormUpdateValue(newValues.flat(), dataformContext.data ?? {}),
        {}
      ),
      () => {
        setLoading(false);
        onClose();
        dataformContext.formStateDispatch({ type: 'VALIDATE' });
      }
    );
  }, [
    dataformContext,
    currentReceiptCertifiers,
    receiverValue,
    currentDecisionCertifiers,
    decisionValue,
    controlValue,
    currentControlCertifiers,
    addFakturaattest,
    deleteFakturaattest,
    onClose,
  ]);

  useDeepCompareEffect(() => {
    setReceiverValue(currentReceiptCertifiers);
    setDecisionValue(currentDecisionCertifiers);
    setControlValue(currentControlCertifiers);
    setLoading(certifiersQuery.loading);
  }, [
    certifiersQuery.loading,
    currentReceiptCertifiers,
    currentDecisionCertifiers,
    currentControlCertifiers,
  ]);

  return (
    <Dialog
      classes={{ paper: isSmallScreen ? undefined : classes.paper }}
      fullscreen={isSmallScreen}
      open={props.open}
      onClose={() => {
        dataformContext?.formStateDispatch({ type: 'RESET' });
        props.onClose();
        dataformContext?.formStateDispatch({ type: 'VALIDATE' });
      }}
      title="Hantera Attestanter"
      footerActions={[
        {
          label: 'Avbryt',
          buttonProps: {
            disabled: loading || certifiersQuery.loading,
          },
          onClick: () => {
            dataformContext?.formStateDispatch({ type: 'RESET' });
            props.onClose();
            dataformContext?.formStateDispatch({ type: 'VALIDATE' });
          },
        },
        {
          label: 'Uppdatera',
          buttonProps: {
            disabledReason: isUpdateButtonDisabled(),
            disabled: !isEmpty(isUpdateButtonDisabled()),
          },

          onClick: onUpdateClick,
        },
      ]}
    >
      <EDOverlay
        overlay={loading && <EDAvenyProgress />}
        classes={{ overlay: classes.savingOverlay }}
      >
        {(loading && certifiersQuery.loading) || !certifiersQuery.data ? (
          <EDAvenyProgress />
        ) : (
          <>
            <EDAutoCompleteEdit<AllCertifiers_db_LRAttestratt_All, true>
              classes={{
                tag: classes.tag,
                inputRoot:
                  receiverValue.length >= 3 ? classes.inputRoot : undefined,
              }}
              disabled={
                loading ||
                decisionCertificates.some(
                  value =>
                    value.STATUS === TEDLRFakturaAttestSTATUSEnum.ATTESTERAD
                )
              }
              fullWidth
              getOptionDisabled={getOptionDisabled(receiptCertificates)}
              getOptionLabel={getOptionLabel}
              getOptionSelected={getOptionSelected}
              hideClear
              label="Mottagningsattestanter"
              multiple
              onChange={getReceiverValue}
              options={allCertifierData.filter(
                data => data.MOTTAGN_ATTEST === 'JA'
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map(
                  getChip(
                    getTagProps,
                    tagValue,
                    receiptCertificates,
                    TEDLRFakturaAttestAttestTypEnum.MOTTAGNINGSATTEST
                  )
                )
              }
              value={receiverValue}
              testId={'maAutoComplete'}
            />

            <EDAutoCompleteEdit<AllCertifiers_db_LRAttestratt_All, true>
              classes={{
                tag: classes.tag,
                inputRoot:
                  decisionValue.length >= 3 ? classes.inputRoot : undefined,
              }}
              disabled={
                loading ||
                (decisionCertificates.length !== 0 &&
                  decisionCertificates.every(
                    value =>
                      value.STATUS === TEDLRFakturaAttestSTATUSEnum.ATTESTERAD
                  ))
              }
              fullWidth
              getOptionDisabled={getOptionDisabled(decisionCertificates)}
              getOptionLabel={getOptionLabel}
              getOptionSelected={getOptionSelected}
              hideClear
              label="Beslutsattestanter"
              multiple
              onChange={getDecisionValue}
              options={allCertifierData.filter(
                data => data.BESLUT_ATTEST === 'JA'
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map(
                  getChip(
                    getTagProps,
                    tagValue,
                    decisionCertificates,
                    TEDLRFakturaAttestAttestTypEnum.BESLUTSATTEST
                  )
                )
              }
              value={decisionValue}
              testId={'baAutoComplete'}
            />

            <EDDivider marginY={1.5} variant="fullWidth" />
            <EDTypography>
              Redan upplagda kontrollattestanter kan inte tas bort här.
            </EDTypography>

            <EDAutoCompleteEdit<AllCertifiers_db_LRAttestratt_All, true>
              classes={{
                tag: classes.tag,
                inputRoot:
                  controlValue.length >= 3 ? classes.inputRoot : undefined,
              }}
              disabled={loading}
              fullWidth
              getOptionDisabled={option =>
                currentControlCertifiers.some(
                  currentControlCertifier =>
                    currentControlCertifier.id === option.id
                )
              }
              getOptionLabel={getOptionLabel}
              getOptionSelected={getOptionSelected}
              hideClear
              label="Kontrollattestanter"
              multiple
              onChange={getControlValue}
              options={allCertifierData.filter(
                data => data.BESLUT_ATTEST === 'JA'
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map(
                  getChip(
                    getTagProps,
                    tagValue,
                    controlCertificates,
                    TEDLRFakturaAttestAttestTypEnum.KONTROLLATTEST
                  )
                )
              }
              value={controlValue}
              testId={'kaAutoComplete'}
            />
            <EDTypography italic>
              Manuella kontrollattestanter ingår ej i det normala attestflödet
              utan sätts här i undantagsfall. Övriga kontrollattestanter
              fungerar som vanligt.
            </EDTypography>
          </>
        )}
      </EDOverlay>
    </Dialog>
  );
};

export default FakturaAttestHandleCertifiersModal;
